import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import {
  getClientOpportunity,
  updateClientOpportunityStatus,
  addClientOpportunityNote,
  updateClientOpportunityFollowUp,
  updateClientOpportunity,
} from "../../api/clientOpportunitiesApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import OpportunityDocumentsPanel from "../../components/clientPortal/OpportunityDocumentsPanel";
import {
  formatClientDate,
  formatClientDateTime,
  formatStatusLabel,
} from "../../utils/clientPortalFormat";
import {
  formatMidpointMediaOpportunityStatus,
  isGolfHomesView,
  isHiringOpportunity,
  isMidpointMediaView,
} from "../../utils/clientViewTheme";
import "../../styles/clientOpportunityDetail.css";
import "../../styles/golfHomesOpportunityDetail.css";

const PIPELINE_STAGES = [
  { key: "new", label: "New", color: "#2563eb" },
  { key: "assigned", label: "Assigned", color: "#7c3aed" },
  { key: "contacted", label: "Contacted", color: "#0891b2" },
  { key: "under_contract", label: "Under Contract", color: "#d97706" },
  { key: "closed_won", label: "Closed / Won", color: "#16a34a" },
];

const MM_PIPELINE_STAGES = [
  { key: "new", label: "New", color: "#2563eb" },
  { key: "assigned", label: "Reviewing", color: "#7c3aed" },
  { key: "contacted", label: "Contacted", color: "#0891b2" },
  { key: "under_contract", label: "In progress", color: "#d97706" },
  { key: "closed_won", label: "Hired / Won", color: "#16a34a" },
];

const GH_PIPELINE_COLORS = {
  new: "#3b82f6",
  assigned: "#7c3aed",
  contacted: "#0891b2",
  under_contract: "#c9a227",
  closed_won: "#165a39",
};

const QUICK_STATUS_KEYS = ["new", "contacted", "under_contract", "closed_won", "on_hold"];

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "new") return "opp-badge--blue";
  if (s === "assigned" || s === "qualified") return "opp-badge--purple";
  if (s === "contacted") return "opp-badge--cyan";
  if (s === "under_contract") return "opp-badge--amber";
  if (s === "closed_won" || s === "won" || s === "completed") return "opp-badge--green";
  if (s === "on_hold") return "opp-badge--muted";
  if (s === "cancelled" || s === "lost" || s === "failed") return "opp-badge--red";
  return "opp-badge--muted";
}

function isOverdue(followUp) {
  if (!followUp) return false;
  return new Date(followUp) < new Date();
}

function formatBudget(min, max) {
  const fmt = (n) =>
    Number(n).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `From ${fmt(min)}`;
  if (max != null) return `Up to ${fmt(max)}`;
  return null;
}

function formatOpportunityType(type) {
  if (!type) return "—";
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getNextAction(status, { isGolfHomes, isMidpointMedia } = {}) {
  const s = String(status || "").toLowerCase();
  if (isMidpointMedia) {
    if (s === "new" || s === "assigned")
      return {
        text: "Review the application and reach out within 24 hours — email, call, or WhatsApp.",
        cta: "Mark Contacted",
        status: "contacted",
      };
    if (s === "contacted")
      return {
        text: "Schedule an interview or move them into active screening.",
        cta: "In progress",
        status: "under_contract",
      };
    if (s === "under_contract")
      return {
        text: "Close the loop when they are hired, declined, or the role is filled.",
        cta: "Hired / Won",
        status: "closed_won",
      };
    return null;
  }
  if (isGolfHomes) {
    if (s === "new" || s === "assigned")
      return {
        text: "Reach out while they're actively exploring golf communities — call or email within 24 hours.",
        cta: "Mark Contacted",
        status: "contacted",
      };
    if (s === "contacted")
      return {
        text: "Send curated listings or schedule a showing for their preferred community or course.",
        cta: "Under Contract",
        status: "under_contract",
      };
    if (s === "under_contract")
      return {
        text: "Confirm closing timeline and any community or membership requirements.",
        cta: "Closed / Won",
        status: "closed_won",
      };
    return null;
  }
  if (s === "new")
    return { text: "Make first contact while interest is fresh.", cta: "Mark as Contacted", status: "contacted" };
  if (s === "assigned")
    return { text: "Reach out and confirm their needs and timeline.", cta: "Mark as Contacted", status: "contacted" };
  if (s === "contacted")
    return { text: "Move forward when terms or a showing are in progress.", cta: "Under Contract", status: "under_contract" };
  if (s === "under_contract")
    return { text: "Close the loop when the deal is finalized.", cta: "Closed / Won", status: "closed_won" };
  return null;
}

function buildEditForm(opp) {
  return {
    title: opp?.title || "",
    opportunityType: opp?.opportunityType || "",
    contactFirstName: opp?.contactFirstName || "",
    contactLastName: opp?.contactLastName || "",
    contactEmail: opp?.contactEmail || "",
    contactPhone: opp?.contactPhone || "",
    preferredContactMethod: opp?.preferredContactMethod || "",
    interestedIn: opp?.interestedIn || "",
    budgetMin: opp?.budgetMin != null ? String(opp.budgetMin) : "",
    budgetMax: opp?.budgetMax != null ? String(opp.budgetMax) : "",
    summary: opp?.summary || "",
    propertyInterest: opp?.propertyInterest || "",
    communityInterest: opp?.communityInterest || "",
    courseInterest: opp?.courseInterest || "",
    financingStatus: opp?.financingStatus || "",
    moveTimeframe: opp?.moveTimeframe || "",
    vendorId: opp?.vendorId || "",
    assignedClientUserId: opp?.assignedClientUserId || "",
  };
}

const ClientOpportunityDetailPage = () => {
  const { clientViewId, opportunityId } = useParams();
  const [clientView, setClientView] = useState(null);
  const [viewName, setViewName] = useState("");
  const [opportunity, setOpportunity] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusValue, setStatusValue] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [noteText, setNoteText] = useState("");
  const [followUpAt, setFollowUpAt] = useState("");
  const [assignmentOptions, setAssignmentOptions] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const paths = useClientPortalPaths();

  const listPath = paths.viewSection(clientViewId, "opportunities");
  const vendorListPath = paths.viewSection(clientViewId, "realtors-vendors");
  const isGolfHomes = isGolfHomesView(clientView);
  const isMidpointMedia = isMidpointMediaView(clientView);
  const isHiring = isHiringOpportunity(opportunity);
  const hideRealEstateFields = isMidpointMedia || isHiring;
  const pipelineStages = isMidpointMedia ? MM_PIPELINE_STAGES : PIPELINE_STAGES;
  const statusLabel = (status) =>
    isMidpointMedia ? formatMidpointMediaOpportunityStatus(status) : formatStatusLabel(status);
  const crmLeadPath =
    isMidpointMedia && opportunity?.leadId ? `${paths.base}/crm/leads/${opportunity.leadId}` : null;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, body] = await Promise.all([
        getClientPortalView(clientViewId),
        getClientOpportunity(clientViewId, opportunityId),
      ]);
      setClientView(viewBody.clientView);
      setViewName(viewBody.clientView?.name || "Client View");
      setOpportunity(body.opportunity);
      setStatusOptions(body.statusOptions || []);
      setAssignmentOptions(body.assignmentOptions || null);
      setStatusValue(body.opportunity?.status || "");
      if (body.opportunity?.nextFollowUpAt) {
        setFollowUpAt(body.opportunity.nextFollowUpAt.slice(0, 16));
      }
    } catch (error) {
      setErrorMessage(error.message || "We could not load this opportunity.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, opportunityId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusSave = async (event, overrideStatus) => {
    event?.preventDefault();
    const nextStatus = overrideStatus || statusValue;
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const body = await updateClientOpportunityStatus(clientViewId, opportunityId, {
        status: nextStatus,
        note: statusNote,
      });
      applyOpportunityResponse(body);
      setStatusValue(body.opportunity?.status || nextStatus);
      setStatusNote("");
      setSuccessMessage("Status updated.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const body = await addClientOpportunityNote(clientViewId, opportunityId, {
        note: noteText,
      });
      applyOpportunityResponse(body);
      setNoteText("");
      setSuccessMessage("Note added to the timeline.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to add note.");
    } finally {
      setSaving(false);
    }
  };

  const applyOpportunityResponse = (body) => {
    setOpportunity(body.opportunity);
    setAssignmentOptions(body.assignmentOptions || null);
    setStatusValue(body.opportunity?.status || "");
    if (body.opportunity?.nextFollowUpAt) {
      setFollowUpAt(body.opportunity.nextFollowUpAt.slice(0, 16));
    }
    return body.opportunity;
  };

  const startEditing = () => {
    setEditForm(buildEditForm(opportunity));
    setEditing(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm(null);
  };

  const handleEditSave = async (event) => {
    event.preventDefault();
    if (!editForm) return;
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const body = await updateClientOpportunity(clientViewId, opportunityId, {
        ...editForm,
        budgetMin: editForm.budgetMin === "" ? null : editForm.budgetMin,
        budgetMax: editForm.budgetMax === "" ? null : editForm.budgetMax,
        vendorId: editForm.vendorId || null,
        assignedClientUserId: editForm.assignedClientUserId || null,
      });
      const updated = applyOpportunityResponse(body);
      setEditForm(buildEditForm(updated));
      setEditing(false);
      setSuccessMessage("Opportunity saved.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to save opportunity.");
    } finally {
      setSaving(false);
    }
  };

  const handleFollowUpSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const body = await updateClientOpportunityFollowUp(
        clientViewId,
        opportunityId,
        followUpAt ? new Date(followUpAt).toISOString() : null
      );
      applyOpportunityResponse(body);
      setSuccessMessage("Follow-up reminder saved.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to update follow-up.");
    } finally {
      setSaving(false);
    }
  };

  const followUpOverdue = useMemo(
    () => opportunity && isOverdue(opportunity.nextFollowUpAt),
    [opportunity]
  );

  const nextAction = useMemo(
    () => (opportunity ? getNextAction(opportunity.status, { isGolfHomes, isMidpointMedia }) : null),
    [opportunity, isGolfHomes, isMidpointMedia]
  );

  const pipelineIndex = useMemo(() => {
    if (!opportunity) return -1;
    return pipelineStages.findIndex((s) => s.key === opportunity.status);
  }, [opportunity, pipelineStages]);

  const highlights = useMemo(() => {
    if (!opportunity) return [];
    const items = [];
    if (hideRealEstateFields) {
      if (opportunity.roleType || opportunity.interestedIn) {
        items.push({
          icon: "🎯",
          label: "Role",
          value: opportunity.roleType || opportunity.interestedIn,
          featured: true,
        });
      }
      if (opportunity.applicantCity) {
        items.push({ icon: "📍", label: "City", value: opportunity.applicantCity });
      }
      if (opportunity.preferredChannel) {
        items.push({ icon: "💬", label: "Preferred channel", value: opportunity.preferredChannel });
      }
      return items;
    }
    if (opportunity.propertyInterest) {
      items.push({
        icon: "🏠",
        label: isGolfHomes ? "Listing / home" : "Property",
        value: opportunity.propertyInterest,
        featured: true,
      });
    }
    if (opportunity.communityInterest) {
      items.push({
        icon: "🏘️",
        label: "Golf community",
        value: opportunity.communityInterest,
      });
    }
    if (opportunity.courseInterest) {
      items.push({
        icon: "⛳",
        label: "Course",
        value: opportunity.courseInterest,
      });
    }
    const budget = formatBudget(opportunity.budgetMin, opportunity.budgetMax);
    if (budget) {
      items.push({ icon: "💰", label: "Budget", value: budget });
    }
    if (!items.length && opportunity.interestedIn) {
      items.push({
        icon: "🎯",
        label: "Interest",
        value: opportunity.interestedIn,
        featured: true,
      });
    }
    return items;
  }, [opportunity, isGolfHomes, hideRealEstateFields]);

  const labeledStatusOptions = useMemo(
    () =>
      (statusOptions || [])
        .filter((opt) => !(isMidpointMedia && opt.key === "completed"))
        .map((opt) => ({
          ...opt,
          label: isMidpointMedia ? formatMidpointMediaOpportunityStatus(opt.key) : opt.label,
        })),
    [statusOptions, isMidpointMedia]
  );

  const quickStatusOptions = useMemo(() => {
    const keys = new Set(QUICK_STATUS_KEYS);
    return labeledStatusOptions.filter((opt) => keys.has(opt.key));
  }, [labeledStatusOptions]);

  const displayName = opportunity?.contactName || opportunity?.title || "Opportunity";

  const stageColors = isGolfHomes
    ? GH_PIPELINE_COLORS
    : Object.fromEntries(pipelineStages.map((s) => [s.key, s.color]));

  if (loading) {
    return <ClientPortalLoading message="Loading lead details…" />;
  }

  if (errorMessage && !opportunity) {
    return (
      <>
        <ClientPortalError message={errorMessage} />
        <p style={{ marginTop: 16 }}>
          <Link to={listPath} className="btn btn-secondary">
            Back to Opportunities &amp; Leads
          </Link>
        </p>
      </>
    );
  }

  if (!opportunity) {
    return <ClientPortalError message="Opportunity not found." />;
  }

  return (
    <div className={`opp-detail${isGolfHomes ? " opp-detail--golf-homes" : ""}`}>
      <header className="opp-detail__page-header">
        <nav className="opp-detail__breadcrumb" aria-label="Breadcrumb">
          <Link to={paths.view(clientViewId)}>{viewName}</Link>
          <span className="opp-detail__breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <Link to={listPath}>Opportunities &amp; Leads</Link>
          <span className="opp-detail__breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <span className="opp-detail__breadcrumb-current">{displayName}</span>
        </nav>
        <div className="opp-detail__header-actions">
          {opportunity.canUpdate && !editing ? (
            <button type="button" className="btn btn-primary" onClick={startEditing}>
              Edit opportunity
            </button>
          ) : null}
          {crmLeadPath ? (
            <Link to={crmLeadPath} className="btn btn-secondary">
              Open CRM lead
            </Link>
          ) : null}
          <Link to={listPath} className="opp-detail__back-link">
            ← Back to list
          </Link>
        </div>
      </header>

      <section className="panel-card opp-detail__summary" aria-labelledby="opp-detail-heading">
        <div className="opp-detail__summary-row">
          <div className="opp-detail__summary-main">
            <h1 id="opp-detail-heading" className="opp-detail__title">
              {displayName}
            </h1>
            {opportunity.title && opportunity.contactName && opportunity.title !== opportunity.contactName ? (
              <p className="opp-detail__subtitle">{opportunity.title}</p>
            ) : null}
            <div className="opp-detail__summary-meta">
              <span className={`opp-badge ${statusBadgeClass(opportunity.status)}`}>
                {statusLabel(opportunity.status)}
              </span>
              {opportunity.opportunityType ? (
                <span className="opp-detail__type-pill">
                  {formatOpportunityType(opportunity.opportunityType)}
                </span>
              ) : null}
              {opportunity.opportunityNumber ? (
                <span className="opp-detail__ref">{opportunity.opportunityNumber}</span>
              ) : null}
              <span className="opp-detail__summary-dates">
                Submitted {formatClientDateTime(opportunity.submittedAt)}
                {" · "}Updated {formatClientDateTime(opportunity.lastUpdatedAt)}
              </span>
              {(opportunity.assignedToName || opportunity.vendorName) ? (
                <span className="opp-detail__summary-assignments">
                  {opportunity.assignedToName ? (
                    <span>Employee: {opportunity.assignedToName}</span>
                  ) : null}
                  {opportunity.vendorName ? (
                    <span>
                      {opportunity.assignedToName ? " · " : ""}
                      {isGolfHomes ? "Realtor/vendor" : "Vendor"}:{" "}
                      {opportunity.vendorId ? (
                        <Link
                          to={`${vendorListPath}/${opportunity.vendorId}`}
                          className="opp-detail__link"
                        >
                          {opportunity.vendorName}
                        </Link>
                      ) : (
                        opportunity.vendorName
                      )}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
          </div>
          {(opportunity.contactEmail || opportunity.contactPhone) ? (
            <div className="opp-detail__summary-actions">
              {opportunity.contactPhone ? (
                <a href={`tel:${opportunity.contactPhone}`} className="opp-detail__action-link">
                  Call
                </a>
              ) : null}
              {opportunity.contactEmail ? (
                <a href={`mailto:${opportunity.contactEmail}`} className="opp-detail__action-link opp-detail__action-link--secondary">
                  Email
                </a>
              ) : null}
              {opportunity.contactPhone &&
              String(opportunity.preferredChannel || opportunity.preferredContactMethod || "")
                .toLowerCase()
                .includes("whatsapp") ? (
                <a
                  href={`https://wa.me/${String(opportunity.contactPhone).replace(/\D/g, "")}`}
                  className="opp-detail__action-link opp-detail__action-link--secondary"
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      {followUpOverdue ? (
        <div className="opp-detail__alert opp-detail__alert--warn" role="alert">
          <strong>Follow-up overdue</strong>
          <span>
            Was scheduled for {formatClientDateTime(opportunity.nextFollowUpAt)}. Update status or
            set a new reminder in the workflow panel.
          </span>
        </div>
      ) : null}

      {successMessage ? (
        <div className="opp-detail__alert opp-detail__alert--success" role="status">
          {successMessage}
          <button type="button" className="opp-detail__alert-dismiss" onClick={() => setSuccessMessage("")}>
            Dismiss
          </button>
        </div>
      ) : null}

      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {/* Interest highlights — especially useful for Golf Homes */}
      {highlights.length > 0 ? (
        <div className="opp-detail__highlights">
          {highlights.map((item) => (
            <div
              key={item.label}
              className={`opp-detail__highlight-card${item.featured ? " opp-detail__highlight-card--featured" : ""}`}
            >
              <div className="opp-detail__highlight-icon" aria-hidden="true">
                {item.icon}
              </div>
              <div className="opp-detail__highlight-label">{item.label}</div>
              <div className="opp-detail__highlight-value">{item.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="opp-detail__insight-row">
        <section className="panel-card opp-detail__pipeline-card">
          <h3 className="opp-section-heading">
            {isGolfHomes ? "Pipeline" : "Pipeline progress"}
          </h3>
          <div className="opp-detail__pipeline-track" aria-hidden="true">
            <div
              className="opp-detail__pipeline-fill"
              style={{
                width: `${Math.max(0, pipelineIndex) / Math.max(pipelineStages.length - 1, 1) * 100}%`,
              }}
            />
          </div>
          <div className="opp-detail__pipeline" role="list">
            {pipelineStages.map((stage, i) => {
              const isPast = pipelineIndex > i;
              const isCurrent = opportunity.status === stage.key;
              const isTerminal =
                opportunity.status === "on_hold" ||
                opportunity.status === "cancelled" ||
                opportunity.status === "failed";
              const color = stageColors[stage.key] || stage.color;
              return (
                <div
                  key={stage.key}
                  role="listitem"
                  className={`opp-detail__pipeline-step${isCurrent ? " is-current" : ""}${isPast ? " is-past" : ""}${isTerminal && !isCurrent && !isPast ? " is-muted" : ""}`}
                >
                  <span
                    className="opp-detail__pipeline-dot"
                    style={{
                      background: isPast || isCurrent ? color : "var(--cp-border, #e5e7eb)",
                      borderColor: isPast || isCurrent ? color : "var(--cp-border, #e5e7eb)",
                    }}
                  />
                  <span className="opp-detail__pipeline-label">{stage.label}</span>
                </div>
              );
            })}
          </div>
          {(opportunity.status === "on_hold" ||
            opportunity.status === "cancelled" ||
            opportunity.status === "failed") && (
            <p className="muted opp-detail__pipeline-aside">
              Current status: {statusLabel(opportunity.status)}
            </p>
          )}
        </section>

        {nextAction && opportunity.canUpdate && opportunity.status !== "closed_won" ? (
          <section className="opp-detail__next-action" aria-label="Suggested next step">
            <div className="opp-detail__next-action-copy">
              <span className="opp-detail__next-action-label">
                {isGolfHomes ? "Recommended next step" : "Suggested next step"}
              </span>
              <p>{nextAction.text}</p>
            </div>
            <button
              type="button"
              className="btn btn-primary opp-detail__next-action-btn"
              disabled={saving}
              onClick={(e) => handleStatusSave(e, nextAction.status)}
            >
              {nextAction.cta}
            </button>
          </section>
        ) : null}
      </div>

      <div className="opp-detail__layout">
        <div className="opp-detail__main">
          {!editing && opportunity.summary ? (
            <section className="panel-card opp-detail__message-hero">
              <h3 className="opp-section-heading">
                {isGolfHomes
                  ? "What they told you"
                  : hideRealEstateFields
                    ? "Application summary"
                    : "Message from visitor"}
              </h3>
              <blockquote className="opp-detail__visitor-message">{opportunity.summary}</blockquote>
            </section>
          ) : null}

          {!editing ? (
            <OpportunityDocumentsPanel
              clientViewId={clientViewId}
              opportunityId={opportunityId}
              canEdit={Boolean(opportunity.canUpdate)}
              intro={
                hideRealEstateFields
                  ? "Attach résumés, interview notes, and other hiring files."
                  : undefined
              }
            />
          ) : null}

          {editing && editForm ? (
            <section className="panel-card opp-detail__edit-card">
              <div className="opp-detail__edit-header">
                <h3 className="opp-section-heading">Edit opportunity</h3>
                <p className="muted opp-detail__edit-intro">
                  Update contact details, search criteria, and who is working this lead.
                </p>
              </div>
              <form onSubmit={handleEditSave} className="opp-detail__edit-form">
                <div className="opp-detail__edit-section">
                  <h4>Record</h4>
                  <div className="opp-detail__edit-grid">
                    <label className="opp-detail__field">
                      <span>Opportunity title</span>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                        required
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Type</span>
                      <input
                        type="text"
                        value={editForm.opportunityType}
                        onChange={(e) => setEditForm((f) => ({ ...f, opportunityType: e.target.value }))}
                        placeholder="Buyer, Seller…"
                        disabled={saving}
                      />
                    </label>
                  </div>
                </div>

                <div className="opp-detail__edit-section">
                  <h4>Contact</h4>
                  <div className="opp-detail__edit-grid">
                    <label className="opp-detail__field">
                      <span>First name</span>
                      <input
                        type="text"
                        value={editForm.contactFirstName}
                        onChange={(e) => setEditForm((f) => ({ ...f, contactFirstName: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Last name</span>
                      <input
                        type="text"
                        value={editForm.contactLastName}
                        onChange={(e) => setEditForm((f) => ({ ...f, contactLastName: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Email</span>
                      <input
                        type="email"
                        value={editForm.contactEmail}
                        onChange={(e) => setEditForm((f) => ({ ...f, contactEmail: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Phone</span>
                      <input
                        type="tel"
                        value={editForm.contactPhone}
                        onChange={(e) => setEditForm((f) => ({ ...f, contactPhone: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field opp-detail__field--full">
                      <span>Preferred contact method</span>
                      <select
                        value={editForm.preferredContactMethod}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, preferredContactMethod: e.target.value }))
                        }
                        disabled={saving}
                      >
                        <option value="">—</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="text">Text</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="opp-detail__edit-section">
                  <h4>
                    {isGolfHomes
                      ? "Search criteria"
                      : hideRealEstateFields
                        ? "Application details"
                        : "Interest & details"}
                  </h4>
                  <div className="opp-detail__edit-grid">
                    <label className="opp-detail__field opp-detail__field--full">
                      <span>
                        {isGolfHomes
                          ? "What they told you"
                          : hideRealEstateFields
                            ? "Application summary"
                            : "Visitor message"}
                      </span>
                      <textarea
                        value={editForm.summary}
                        onChange={(e) => setEditForm((f) => ({ ...f, summary: e.target.value }))}
                        rows={3}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>{hideRealEstateFields ? "Role" : "Interested in"}</span>
                      <input
                        type="text"
                        value={editForm.interestedIn}
                        onChange={(e) => setEditForm((f) => ({ ...f, interestedIn: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    {hideRealEstateFields ? null : (
                      <>
                    <label className="opp-detail__field">
                      <span>Financing</span>
                      <input
                        type="text"
                        value={editForm.financingStatus}
                        onChange={(e) => setEditForm((f) => ({ ...f, financingStatus: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Budget min</span>
                      <input
                        type="number"
                        min="0"
                        value={editForm.budgetMin}
                        onChange={(e) => setEditForm((f) => ({ ...f, budgetMin: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Budget max</span>
                      <input
                        type="number"
                        min="0"
                        value={editForm.budgetMax}
                        onChange={(e) => setEditForm((f) => ({ ...f, budgetMax: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Property / listing</span>
                      <input
                        type="text"
                        value={editForm.propertyInterest}
                        onChange={(e) => setEditForm((f) => ({ ...f, propertyInterest: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Community</span>
                      <input
                        type="text"
                        value={editForm.communityInterest}
                        onChange={(e) => setEditForm((f) => ({ ...f, communityInterest: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Course</span>
                      <input
                        type="text"
                        value={editForm.courseInterest}
                        onChange={(e) => setEditForm((f) => ({ ...f, courseInterest: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                    <label className="opp-detail__field">
                      <span>Move timeframe</span>
                      <input
                        type="text"
                        value={editForm.moveTimeframe}
                        onChange={(e) => setEditForm((f) => ({ ...f, moveTimeframe: e.target.value }))}
                        disabled={saving}
                      />
                    </label>
                      </>
                    )}
                  </div>
                  {opportunity.source ? (
                    <p className="muted opp-detail__hint">Source: {opportunity.source} (read-only)</p>
                  ) : null}
                </div>

                {assignmentOptions ? (
                  <div className="opp-detail__edit-section">
                    <h4>Assignments</h4>
                    <div className="opp-detail__edit-grid">
                      <label className="opp-detail__field">
                        <span>{isGolfHomes ? "Realtor or vendor" : "Vendor"}</span>
                        <select
                          value={editForm.vendorId}
                          onChange={(e) => setEditForm((f) => ({ ...f, vendorId: e.target.value }))}
                          disabled={saving}
                        >
                          <option value="">Not linked</option>
                          {assignmentOptions.vendors.map((v) => (
                            <option key={v.vendorId} value={v.vendorId}>
                              {v.vendorName}
                              {v.vendorType ? ` (${v.vendorType})` : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="opp-detail__field">
                        <span>{isGolfHomes ? "Golf Homes employee" : "Assigned employee"}</span>
                        <select
                          value={editForm.assignedClientUserId}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, assignedClientUserId: e.target.value }))
                          }
                          disabled={saving}
                        >
                          <option value="">Unassigned</option>
                          {assignmentOptions.employees.map((emp) => (
                            <option key={emp.clientUserId} value={emp.clientUserId}>
                              {emp.name}
                              {emp.role
                                ? ` (${formatStatusLabel(emp.role.replace(/^CLIENT_/, ""))})`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <p className="muted opp-detail__hint">
                      Manage your directory under{" "}
                      <Link to={vendorListPath}>Realtors &amp; Vendors</Link>.
                    </p>
                  </div>
                ) : null}

                <div className="opp-detail__edit-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelEditing}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </section>
          ) : (
            <div className="opp-detail__info-columns">
              <section className="panel-card opp-detail__info-card">
                <h3 className="opp-section-heading">Contact</h3>
                <dl className="opp-detail__definition-list">
                  <div className="opp-detail__definition-row">
                    <dt>Name</dt>
                    <dd>{opportunity.contactName || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Email</dt>
                    <dd>
                      {opportunity.contactEmail ? (
                        <a href={`mailto:${opportunity.contactEmail}`} className="opp-detail__link">
                          {opportunity.contactEmail}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Phone</dt>
                    <dd>
                      {opportunity.contactPhone ? (
                        <a href={`tel:${opportunity.contactPhone}`} className="opp-detail__link">
                          {opportunity.contactPhone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Preferred contact</dt>
                    <dd>{opportunity.preferredContactMethod || "—"}</dd>
                  </div>
                </dl>
              </section>

              <section className="panel-card opp-detail__info-card">
                <h3 className="opp-section-heading">
                  {isGolfHomes
                    ? "Search criteria"
                    : hideRealEstateFields
                      ? "Application details"
                      : "Interest & details"}
                </h3>
                <dl className="opp-detail__definition-list">
                  <div className="opp-detail__definition-row">
                    <dt>{hideRealEstateFields ? "Role" : "Interested in"}</dt>
                    <dd>{opportunity.roleType || opportunity.interestedIn || "—"}</dd>
                  </div>
                  {hideRealEstateFields ? (
                    <>
                      <div className="opp-detail__definition-row">
                        <dt>City</dt>
                        <dd>{opportunity.applicantCity || "—"}</dd>
                      </div>
                      <div className="opp-detail__definition-row">
                        <dt>Preferred channel</dt>
                        <dd>{opportunity.preferredChannel || opportunity.preferredContactMethod || "—"}</dd>
                      </div>
                    </>
                  ) : (
                    <>
                  <div className="opp-detail__definition-row">
                    <dt>Budget</dt>
                    <dd>{formatBudget(opportunity.budgetMin, opportunity.budgetMax) || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Property / listing</dt>
                    <dd>{opportunity.propertyInterest || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Community</dt>
                    <dd>{opportunity.communityInterest || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Course</dt>
                    <dd>{opportunity.courseInterest || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Financing</dt>
                    <dd>{opportunity.financingStatus || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>Move timeframe</dt>
                    <dd>{opportunity.moveTimeframe || "—"}</dd>
                  </div>
                    </>
                  )}
                  <div className="opp-detail__definition-row">
                    <dt>Source</dt>
                    <dd>{opportunity.source || "—"}</dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>{isGolfHomes ? "Realtor / vendor" : "Vendor"}</dt>
                    <dd>
                      {opportunity.vendorName ? (
                        opportunity.vendorId ? (
                          <Link
                            to={`${vendorListPath}/${opportunity.vendorId}`}
                            className="opp-detail__link"
                          >
                            {opportunity.vendorName}
                            {opportunity.vendorType ? ` (${opportunity.vendorType})` : ""}
                          </Link>
                        ) : (
                          opportunity.vendorName
                        )
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                  <div className="opp-detail__definition-row">
                    <dt>{isGolfHomes ? "Golf Homes employee" : "Assigned employee"}</dt>
                    <dd>{opportunity.assignedToName || "—"}</dd>
                  </div>
                </dl>
              </section>
            </div>
          )}

          <details className="panel-card opp-detail__lead-card">
            <summary className="opp-detail__lead-summary">
              {isGolfHomes
                ? "Lead & record references"
                : hideRealEstateFields
                  ? "Application & record references"
                  : "Record references"}
            </summary>
            <div className="opp-detail__lead-explainer">
              <div className="opp-detail__lead-flow">
                <div className="opp-detail__lead-flow-step">
                  <span className="opp-detail__lead-flow-icon opp-detail__lead-flow-icon--lead">1</span>
                  <div>
                    <strong>Inbound lead</strong>
                    <p className="muted">
                      {isGolfHomes
                        ? `Reached out through your Golf Homes site${opportunity.submittedAt ? ` on ${formatClientDate(opportunity.submittedAt)}` : ""}.`
                        : hideRealEstateFields
                          ? `Applied${opportunity.submittedAt ? ` on ${formatClientDate(opportunity.submittedAt)}` : ""}.`
                          : `Submitted interest on your site${opportunity.submittedAt ? ` on ${formatClientDate(opportunity.submittedAt)}` : ""}.`}
                    </p>
                  </div>
                </div>
                <div className="opp-detail__lead-flow-step">
                  <span className="opp-detail__lead-flow-icon opp-detail__lead-flow-icon--opp">2</span>
                  <div>
                    <strong>Opportunity</strong>
                    <p className="muted">
                      {hideRealEstateFields
                        ? "Status changes and notes build the activity timeline as you work the application."
                        : "Status changes and notes build the activity timeline as you work the deal."}
                    </p>
                  </div>
                </div>
              </div>
              <dl className="opp-detail__lead-meta">
                {opportunity.leadNumber ? (
                  <>
                    <dt>Lead reference</dt>
                    <dd>
                      {crmLeadPath ? (
                        <Link to={crmLeadPath} className="opp-detail__link">
                          {opportunity.leadNumber}
                        </Link>
                      ) : (
                        opportunity.leadNumber
                      )}
                    </dd>
                  </>
                ) : null}
                {opportunity.leadSource ? (
                  <>
                    <dt>Lead source</dt>
                    <dd>{opportunity.leadSource}</dd>
                  </>
                ) : null}
                <dt>Opportunity reference</dt>
                <dd>{opportunity.opportunityNumber || opportunity.opportunityId}</dd>
              </dl>
            </div>
          </details>

          <section className="panel-card opp-detail__timeline-card">
            <h3 className="opp-section-heading">Activity timeline</h3>
            {!opportunity.activities?.length ? (
              <div className="opp-detail__timeline-empty">
                <p>No activity yet</p>
                <p className="muted">Add a note or update status to start building history.</p>
              </div>
            ) : (
              <ul className="opp-detail__timeline">
                {[...opportunity.activities].reverse().map((item) => (
                  <li key={item.activityId} className="opp-detail__timeline-item">
                    <span className="opp-detail__timeline-dot" aria-hidden="true" />
                    <div className="opp-detail__timeline-body">
                      <div className="opp-detail__timeline-head">
                        <strong>{item.subject || formatOpportunityType(item.activityType)}</strong>
                        <time className="opp-detail__timeline-time" dateTime={item.createdAt}>
                          {formatClientDateTime(item.createdAt)}
                        </time>
                      </div>
                      <p className="opp-detail__timeline-meta">{item.authorName}</p>
                      {item.notes ? <p className="opp-detail__timeline-note">{item.notes}</p> : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="opp-detail__aside">
          {opportunity.nextFollowUpAt ? (
            <section
              className={`panel-card opp-detail__followup-card${followUpOverdue ? " opp-detail__followup-card--overdue" : ""}`}
            >
              <h3 className="opp-section-heading">Follow-up reminder</h3>
              <p className={`opp-detail__followup-date${followUpOverdue ? " is-overdue" : ""}`}>
                {formatClientDateTime(opportunity.nextFollowUpAt)}
              </p>
              {followUpOverdue ? <span className="opp-overdue-tag">Overdue</span> : null}
            </section>
          ) : null}

          {opportunity.canUpdate ? (
            <section
              className={`panel-card opp-detail__action-card${isGolfHomes ? " opp-detail__workflow-card" : ""}`}
            >
              <h3 className="opp-section-heading">
                {isGolfHomes ? "Workflow" : "Take action"}
              </h3>

              {quickStatusOptions.length > 0 ? (
                <div className="opp-detail__workflow-block">
                  <p className="opp-detail__workflow-block-label">Quick status</p>
                  <div className="opp-detail__status-chips" role="group" aria-label="Quick status update">
                    {quickStatusOptions.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`opp-detail__status-chip${statusValue === opt.key ? " is-active" : ""}`}
                        disabled={saving}
                        onClick={() => handleStatusSave(null, opt.key)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="opp-detail__workflow-block">
                <p className="opp-detail__workflow-block-label">Update status</p>
                <form onSubmit={handleStatusSave} className="opp-detail__form">
                  <label className="opp-detail__field">
                    <span className="sr-only">Status</span>
                    <select
                      value={statusValue}
                      onChange={(e) => setStatusValue(e.target.value)}
                      disabled={saving}
                      aria-label="Status"
                    >
                      {labeledStatusOptions.map((opt) => (
                        <option key={opt.key} value={opt.key}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="opp-detail__field">
                    <span>Note (optional)</span>
                    <textarea
                      value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value)}
                      rows={2}
                      placeholder={
                        isGolfHomes
                          ? "e.g. Left voicemail, sent community guide…"
                          : "Context for this status change…"
                      }
                      disabled={saving}
                    />
                  </label>
                  <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                    {saving ? "Saving…" : "Save status"}
                  </button>
                </form>
              </div>

              <div className="opp-detail__workflow-block">
                <p className="opp-detail__workflow-block-label">Follow-up reminder</p>
                <form onSubmit={handleFollowUpSave} className="opp-detail__form">
                  <label className="opp-detail__field">
                    <span className="sr-only">Next follow-up</span>
                    <input
                      type="datetime-local"
                      value={followUpAt}
                      onChange={(e) => setFollowUpAt(e.target.value)}
                      disabled={saving}
                      aria-label="Next follow-up"
                    />
                  </label>
                  {opportunity.nextFollowUpAt && !followUpOverdue ? (
                    <p className="muted opp-detail__hint">
                      Currently {formatClientDateTime(opportunity.nextFollowUpAt)}
                    </p>
                  ) : null}
                  <button type="submit" className="btn btn-secondary btn-block" disabled={saving}>
                    {saving ? "Saving…" : "Save reminder"}
                  </button>
                </form>
              </div>

              <div className="opp-detail__workflow-block">
                <p className="opp-detail__workflow-block-label">Add note</p>
                <form onSubmit={handleAddNote} className="opp-detail__form">
                  <label className="opp-detail__field">
                    <span className="sr-only">Note</span>
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      rows={4}
                      required
                      placeholder={
                        isGolfHomes
                          ? "Showing feedback, community preferences, next steps…"
                          : hideRealEstateFields
                            ? "Interview notes, follow-ups, hiring decision…"
                            : "Call summary, showing feedback, next steps…"
                      }
                      disabled={saving}
                      aria-label="Note"
                    />
                  </label>
                  <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                    {saving ? "Saving…" : "Add to timeline"}
                  </button>
                </form>
              </div>
            </section>
          ) : (
            <section className="panel-card opp-detail__action-card">
              <p className="muted">You have read-only access to leads on this view.</p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ClientOpportunityDetailPage;
